import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Icon, Panel, Tooltip, Typography } from "@/components/atoms";
import { Restricted } from "@/components/pages";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { useVehicleTypeMigrationCheck } from "@/hooks/useVehicleTypeMigrationCheck";
import { useGetVehicleFormOptionsQuery, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useUpdateAffiliateVehicleMutation } from "@/redux/apis/vehicleGuest/vehicleGuestApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, capitalize, getAssetUrl } from "@/utils";
import { PageHeader } from "../../PageHeader/PageHeader";
import { ReactivateVehicleModal } from "../ArchivedVehicles/modals/ReactivateVehicleModal";
import { UnarchiveVehicleModal } from "../ArchivedVehicles/modals/UnarchiveVehicleModal";
import { getInsuranceStatus, getRegistrationStatus } from "../MyVehicles/helpers";
import { SelectVehicleTypeModal } from "../AffiliateVehicles/modals/SelectVehicleTypeModal";
import { VehicleTypesSetupModal } from "../VehicleTypesSetupModal";
import { ArchiveVehicleModal } from "../MyVehicles/modals/ArchiveVehicleModal";
import { VehicleTypesModal } from "../common/VehicleTypesModal";
import { renderVehicleTypeCell } from "../helpers";
import { VehicleDetailCard } from "./common/VehicleDetailCard";
import { HistoryLogsDetailCard } from "./components/HistoryLogsDetailCard";
import { RegistrationExpiryDetailCard } from "./components/RegistrationExpiryDetailCard";
import { VehicleAccessDetailCard } from "./components/VehicleAccessDetailCard";
import { VehicleAccreditationDetailCard } from "./components/VehicleAccreditationDetailCard";
import { VehicleCategoryDetailCard } from "./components/VehicleCategoryDetailCard";
import { VehicleInsuranceDetailCard } from "./components/VehicleInsuranceDetailCard";
import { VehicleOperatorNotesDetailCard } from "./components/VehicleOperatorNotesDetailCard";
import { useViewVehicleContext, ViewVehicleProvider } from "./context";
import { VehicleAccessModal } from "./features/VehicleAccess/VehicleAccessModal";
import { VehicleCategoryModal } from "./features/VehicleCategory/VehicleCategoryModal";
import { VehicleDocuments } from "./features/VehicleDocuments/VehicleDocuments";
import { VehicleHistoryLogsModal } from "./features/VehicleHistoryLogs/VehicleHistoryLogsModal";
import { VehicleOperatorNotesModal } from "./features/VehicleOperatorNotes/VehicleOperatorNotesModal";

export const ViewVehiclePanel = () => (
  <ViewVehicleProvider>
    <Content />
  </ViewVehicleProvider>
);

const Content = () => {
  const { pathname } = useLocation();
  const { id: operatorId } = useOperator();
  const {
    vehicle: {
      metadata: { isArchived },
      owner,
    },
    isLegacy,
  } = useViewVehicleContext();
  const isAffiliate = owner.uuid !== operatorId;

  if (pathname.includes("/vehicles/my-vehicles") && isAffiliate) return <Restricted />;
  if (pathname.includes("/vehicles/affiliate-vehicles") && !isAffiliate) return <Restricted />;
  if (pathname.includes("/vehicles/archived-vehicles") && !isArchived) return <Restricted />;

  return (
    <>
      <PageHeader.Actions />
      <div className="flex flex-col gap-5 lg:flex-row">
        <Sidebar isAffiliate={isAffiliate} isLegacy={isLegacy} />
        <div className="flex flex-1 flex-col gap-5">
          <VehicleInformation isAffiliate={isAffiliate} isLegacy={isLegacy} />
          <OtherInformation isAffiliate={isAffiliate} />
          <VehicleDocuments />
        </div>
      </div>
      <Modals />
    </>
  );
};

const Sidebar = ({ isAffiliate, isLegacy }: { isAffiliate: boolean; isLegacy: boolean }) => {
  const { vehicle } = useViewVehicleContext();
  const { avatar, model, registrationNumber, owner, currentDriver } = vehicle;

  return (
    <Panel className="h-fit w-full shrink-0 lg:w-[300px] xl:w-[340px]">
      <div className="flex flex-col gap-5">
        <div className="relative self-start">
          <img
            src={getAssetUrl(avatar, {
              width: 200,
              height: 200,
              background: "transparent",
            })}
            alt={model.model}
            className="h-[100px] w-[100px] shrink-0 rounded-full border border-neutral-mid-gray bg-neutral-surface-gray object-contain"
          />
          <div className="absolute bottom-0 right-0 flex items-center justify-center rounded-full border border-neutral-mid-gray bg-neutral-surface-gray p-[7px]">
            <img
              src={getAssetUrl(model.avatar, {
                width: 36,
                height: 36,
                background: "transparent",
                fit: "contain",
              })}
              alt={`${model.make} ${model.model}`}
              className="h-[18px] w-[18px]"
            />
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Typography variant="h3">{`${model.make} ${model.model}`}</Typography>
            {!vehicle.metadata.isArchived && <VerifiedBadge />}
          </div>
          {registrationNumber && <Typography className="text-neutral-dark-gray">{registrationNumber}</Typography>}
        </div>

        <Rating />

        <div className="space-y-3">
          <SidebarInfoRow label="Owned by" name={owner.name} avatar={owner.avatar} email={isAffiliate ? owner.email : undefined} />
          {currentDriver && <SidebarInfoRow label="Current Driver" name={currentDriver.name} avatar={currentDriver.avatar} />}
          {currentDriver?.provider && (
            <SidebarInfoRow label="Operated by" name={currentDriver.provider.name} avatar={currentDriver.provider.assets.logo} />
          )}
        </div>

        <SidebarActions isAffiliate={isAffiliate} isLegacy={isLegacy} />
      </div>
    </Panel>
  );
};

const SidebarInfoRow = ({ label, name, avatar, email }: { label: string; name: string; avatar: string; email?: string }) => (
  <div>
    <Typography variant="action" className="text-neutral-dark-gray">
      {label}
    </Typography>
    <div className="mt-1 flex items-center gap-2">
      <img
        src={getAssetUrl(avatar, { width: 48, height: 48, fit: "contain" })}
        className="h-6 w-6 rounded-full border border-neutral-mid-gray object-cover"
        alt={name}
      />
      <Typography variant="paragraph">{name}</Typography>
    </div>
    {email && (
      <div className="mt-0.5 flex items-center gap-2 pl-8">
        <Icon name="Sms" variant="Bold" size="sm" className="text-neutral-dark-gray" />
        <Typography className="whitespace-normal text-neutral-dark-gray" variant="small">
          {email}
        </Typography>
      </div>
    )}
  </div>
);

const VerifiedBadge = () => {
  const { vehicle } = useViewVehicleContext();
  const registrationStatus = getRegistrationStatus(vehicle);
  const insuranceStatus = getInsuranceStatus(vehicle);

  const status = useMemo(() => {
    if (registrationStatus === "expired" || insuranceStatus === "expired") return "expired";
    if (registrationStatus === "expiring" || insuranceStatus === "expiring") return "expiring";
    return insuranceStatus === "verified" ? registrationStatus : insuranceStatus;
  }, [registrationStatus, insuranceStatus]);

  if (status === "verified" || status === "expiring") {
    return (
      <Tooltip content={status === "expiring" ? "Vehicle is verified but a document is expiring soon" : "Vehicle is verified"} placement="top">
        <Icon name="Verify" variant="Bold" size="lg" className="text-info" />
      </Tooltip>
    );
  }

  return (
    <Tooltip content="Vehicle is unverified. It cannot do any work in the system" placement="top">
      <Icon name="Verify" variant="Bold" size="lg" className="text-neutral-mid-gray" />
    </Tooltip>
  );
};

const Rating = () => {
  const { vehicle } = useViewVehicleContext();
  const { jobsCompleted, rating } = vehicle.rating;

  if (rating || rating === 0) {
    const fullStars = Math.floor(rating);
    const emptyStars = 5 - fullStars;

    return (
      <div className="flex items-center gap-1.5">
        {Array.from({ length: fullStars }, (_, i) => (
          <Icon key={`full-${i}`} name="Star1" className="fill-warning text-warning" size="sm" />
        ))}
        {Array.from({ length: emptyStars }, (_, i) => (
          <Icon key={`empty-${i}`} name="Star1" className="text-neutral-mid-gray" size="sm" />
        ))}
        <Typography variant="action" className="ml-1">
          {rating} ({jobsCompleted} reviews)
        </Typography>
      </div>
    );
  }

  return (
    <Typography variant="paragraph" className="text-neutral-dark-gray">
      {jobsCompleted === 0 ? "No reviews yet" : `${jobsCompleted} reviews`}
    </Typography>
  );
};

const SidebarActions = ({ isAffiliate, isLegacy }: { isAffiliate: boolean; isLegacy: boolean }) => {
  const { id: operatorId } = useOperator();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { vehicle } = useViewVehicleContext();
  const [pendingVehicle, setPendingVehicle] = useState<Vehicle | undefined>(undefined);
  const [updateAffiliateVehicle, { isLoading }] = useUpdateAffiliateVehicleMutation();
  const [pendingVehicleType, setPendingVehicleType] = useState<Vehicle | undefined>();
  const [pendingAssignType, setPendingAssignType] = useState<Vehicle | undefined>();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const { needsMigration: needsTypeMigration, valid: typesValid, total: typesTotal } = useVehicleTypeMigrationCheck();

  const needsVehicleType = !vehicle.vehicleType;

  const handleApprove = (isReapprove?: boolean) => {
    if (needsTypeMigration) {
      setShowSetupModal(true);
      return;
    }
    if (needsVehicleType) {
      setPendingVehicleType(vehicle);
      return;
    }
    updateAffiliateVehicle({
      ownerId: operatorId,
      vehicleId: vehicle.uuid,
      approval_status: "APPROVED",
    })
      .unwrap()
      .then(() => {
        if (isReapprove) addToast("success", "Vehicle reapproved successfully");
        else addToast("success", "Vehicle approved successfully");
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const handleDeny = () => {
    updateAffiliateVehicle({
      ownerId: operatorId,
      vehicleId: vehicle.uuid,
      approval_status: "DENIED",
      approval_reason: "Vehicle denied by operator",
    })
      .unwrap()
      .then(() => {
        addToast("success", "Vehicle denied successfully");
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const handleAssignVehicleType = () => {
    if (needsTypeMigration) {
      setShowSetupModal(true);
      return;
    }
    setPendingAssignType(vehicle);
  };

  if (vehicle.metadata.isArchived) {
    if (isLegacy) {
      return (
        <>
          <Button variant="primary" startIcon="Refresh2" onClick={() => setPendingVehicle(vehicle)}>
            Reactivate Vehicle
          </Button>
          <ReactivateVehicleModal
            pendingReactivate={pendingVehicle}
            setPendingReactivate={setPendingVehicle}
            onSuccess={() => {
              if (pathname.includes("/archived-vehicles/")) {
                navigate("../", { replace: true });
              }
            }}
          />
        </>
      );
    }

    return (
      <>
        <Button variant="primary" startIcon="Refresh2" onClick={() => setPendingVehicle(vehicle)}>
          Unarchive Vehicle
        </Button>
        <UnarchiveVehicleModal vehicle={pendingVehicle} onClose={() => setPendingVehicle(undefined)} />
      </>
    );
  }

  if (isAffiliate) {
    if (isLegacy) return null;

    const status = !vehicle.approval ? "PENDING" : vehicle.approval.status;

    return (
      <>
        <div className="flex flex-col gap-3">
          {status === "PENDING" && (
            <>
              <Button onClick={() => handleApprove()} disabled={isLoading} variant="secondary" className="!border-success text-success">
                Approve Vehicle
              </Button>
              <Button onClick={handleDeny} disabled={isLoading} variant="secondary" className="!border-danger text-danger">
                Deny Vehicle
              </Button>
            </>
          )}
          {status === "DENIED" && (
            <Button onClick={() => handleApprove(true)} disabled={isLoading} variant="secondary" className="!border-success text-success">
              Reapprove Vehicle
            </Button>
          )}
          {status === "APPROVED" && (
            <Button onClick={handleAssignVehicleType} variant="secondary" startIcon="Car">
              {needsVehicleType ? "Set Vehicle Type" : "Change Vehicle Type"}
            </Button>
          )}
        </div>
        <SelectVehicleTypeModal vehicle={pendingVehicleType} onClose={() => setPendingVehicleType(undefined)} />
        <SelectVehicleTypeModal vehicle={pendingAssignType} onClose={() => setPendingAssignType(undefined)} approveOnSave={false} />
        <VehicleTypesSetupModal open={showSetupModal} onClose={() => setShowSetupModal(false)} valid={typesValid} total={typesTotal} />
      </>
    );
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setPendingVehicle(vehicle)}>
        Archive Vehicle
      </Button>
      <ArchiveVehicleModal pendingArchive={pendingVehicle} setPendingArchive={setPendingVehicle} />
    </>
  );
};

const VehicleInformation = ({ isAffiliate, isLegacy }: { isAffiliate: boolean; isLegacy: boolean }) => {
  const navigate = useNavigate();
  const { vehicle } = useViewVehicleContext();
  const { data } = useGetVehicleFormOptionsQuery();

  const colors = useMemo(() => {
    if (!data) return [];
    return data.colors;
  }, [data]);

  const showEditButton = !vehicle.metadata.isArchived && !isAffiliate && !isLegacy;
  const [typesVehicle, setTypesVehicle] = useState<Vehicle | null>(null);

  // Whether any vehicle type is attached (many-to-many, falling back to the legacy single type).
  const hasVehicleTypes = (vehicle.vehicleTypes?.length ?? 0) > 0 || !!vehicle.vehicleType;

  return (
    <Panel>
      <div className="mb-5 flex items-start justify-between">
        <div>
          <Typography variant="h3">Vehicle Information</Typography>
          <Typography className="text-neutral-dark-gray">Review key details and documents associated with this vehicle.</Typography>
        </div>
        {showEditButton && (
          <Button variant="primary" size="sm" onClick={() => navigate("./edit")}>
            Edit Vehicle
          </Button>
        )}
      </div>

      <div className="grid auto-rows-min grid-cols-1 gap-2.5 md:grid-cols-2">
        <VehicleDetailCard
          icon={<Icon name="Calendar" className="text-neutral-black" size="xl" />}
          title="Vehicle Year"
          description={vehicle.year ? vehicle.year.toString() : "-"}
        />
        <VehicleDetailCard
          icon={
            <span
              className="inline-block h-6 w-6 rounded-full border border-neutral-dark-gray"
              style={{
                backgroundColor: colors.find((c) => c.key === vehicle.color)?.hex,
              }}
            />
          }
          title="Vehicle Color"
          description={vehicle.color ? capitalize(vehicle.color.toLowerCase()) : "-"}
        />
        <VehicleDetailCard
          icon={<Icon name="engine" isCustom className="text-neutral-black" size="xl" />}
          title="Engine Type"
          description={capitalize(vehicle.model.engine.toLowerCase())}
        />
        <VehicleDetailCard
          icon={<Icon name="Global" className="text-neutral-black" size="xl" />}
          title="State"
          description={vehicle.state.name}
        />
        <RegistrationExpiryDetailCard />
        <VehicleDetailCard
          icon={<Icon name="Story" className="text-neutral-black" size="xl" />}
          title="VIN"
          description={vehicle.vin ?? "-"}
        />
        <VehicleDetailCard
          icon={<Icon name="Car" className="text-neutral-black" size="xl" />}
          title="Vehicle Type"
          description={renderVehicleTypeCell(
            vehicle,
            setTypesVehicle,
            <Typography variant="paragraph" className="capitalize leading-tight text-neutral-dark-gray">
              No vehicle type assigned
            </Typography>,
            "capitalize leading-tight text-neutral-dark-gray"
          )}
          status={!hasVehicleTypes ? "expiring" : undefined}
          tooltip={
            !hasVehicleTypes ? (
              <div className="max-w-[295px] font-medium normal-case">No vehicle type has been assigned to this vehicle.</div>
            ) : (
              <div className="max-w-[295px] font-medium normal-case">
                Vehicle types you define for your fleet. Vehicles can have multiple types — if a booking requires one of them, that vehicle is
                eligible.
              </div>
            )
          }
        />
        <VehicleCategoryDetailCard />
        <VehicleDetailCard
          icon={<Icon name="Edit2" className="text-neutral-black" size="xl" />}
          title="Nickname"
          description={vehicle.identifier || "-"}
        />
        <VehicleDetailCard
          icon={<Icon name="Setting4" className="text-neutral-black" size="xl" />}
          title="Owner Maintenance Fee"
          description={vehicle.maintenanceFee ? `${vehicle.maintenanceFee}%` : "-"}
        />
        <VehicleDetailCard
          icon={<Icon name="Profile" className="text-neutral-black" size="xl" />}
          title="Passenger Capacity"
          description={vehicle.capacity.pax.toString()}
        />
        <VehicleDetailCard
          icon={<Icon name="Briefcase" className="text-neutral-black" size="xl" />}
          title="Bag Capacity"
          description={vehicle.capacity.bags.toString()}
        />
        <VehicleDetailCard
          icon={<Icon name="dot" isCustom className="text-neutral-black" size="xl" />}
          title="Vehicle has markings (Signage/Decals)?"
          description={vehicle.hasMarkings ? "Yes" : "No"}
        />
        <VehicleInsuranceDetailCard />
        <VehicleAccreditationDetailCard />
      </div>
      <VehicleTypesModal vehicle={typesVehicle} onClose={() => setTypesVehicle(null)} />
    </Panel>
  );
};

const OtherInformation = ({ isAffiliate }: { isAffiliate: boolean }) => {
  const { vehicle } = useViewVehicleContext();

  return (
    <Panel>
      <div className="mb-5">
        <Typography variant="h3">Other Information</Typography>
        <Typography className="text-neutral-dark-gray">View access permissions and activity history for this vehicle</Typography>
      </div>

      <div className="grid auto-rows-min grid-cols-1 gap-2.5 md:grid-cols-2">
        {!vehicle.metadata.isArchived && !isAffiliate && <VehicleAccessDetailCard />}
        <HistoryLogsDetailCard />
        {isAffiliate && <VehicleOperatorNotesDetailCard />}
      </div>
    </Panel>
  );
};

const Modals = () => {
  const { vehicle, isLegacy, activeModal, setActiveModal } = useViewVehicleContext();

  return (
    <>
      <VehicleAccessModal
        vehicle={vehicle}
        open={activeModal === "access-manager"}
        onClose={() => {
          setActiveModal(undefined);
        }}
        isReadOnly={isLegacy}
      />
      <VehicleHistoryLogsModal open={activeModal === "history-logs"} onClose={() => setActiveModal(undefined)} />
      <VehicleOperatorNotesModal open={activeModal === "operator-notes"} onClose={() => setActiveModal(undefined)} isReadOnly={isLegacy} />
      <VehicleCategoryModal open={activeModal === "vehicle-category-modal"} onClose={() => setActiveModal(undefined)} />
    </>
  );
};
